import { getCsrfToken } from '../auth/authApi'
import { apiFetch } from '../../shared/http/apiFetch'
import type {
  ContractInspection,
  IntegrationForm,
  IntegrationStudioSnapshot,
  IntegrationExecutionDetail,
  IntegrationExecutionPage,
  IntegrationImportCandidate,
} from './types'

export type IntegratorContext = { configurationId: string; companyId: string }
export const integratorRoute = (context: IntegratorContext) =>
  `/apl/configuraciones/${context.configurationId}/empresas/${context.companyId}/integrator`
const baseUrl = (context: IntegratorContext) =>
  `/api/v1/apl/configurations/${context.configurationId}/companies/${context.companyId}/integrator`
const payload = (form: IntegrationForm) => ({ ...form,
  id: undefined, hasAuthenticationSecret: undefined })

async function errorCode(response: Response, fallback: string) {
  const payload = await response.json().catch(() => null) as { code?: string } | null
  return payload?.code ?? fallback
}

export async function getIntegrationImportCandidates(context: IntegratorContext,
  sourceCompanyId:string):Promise<IntegrationImportCandidate[]> {
  const query = new URLSearchParams({ sourceCompanyId })
  const response = await apiFetch(`${baseUrl(context)}/import-candidates?${query}`,
    { credentials:'include' })
  if (!response.ok) throw new Error(await errorCode(response,
    'integrator.importSourceUnavailable'))
  return response.json() as Promise<IntegrationImportCandidate[]>
}

export async function importIntegrations(context: IntegratorContext,
  sourceCompanyId:string, integrationIds:string[]):Promise<{
    code:string; importedCount:number; updatedCount:number; skippedCount:number }> {
  const response = await apiFetch(`${baseUrl(context)}/import`, {
    method:'POST', credentials:'include', headers:{
      'Content-Type':'application/json', 'X-CSRF-TOKEN':await getCsrfToken() },
    body:JSON.stringify({ sourceCompanyId, integrationIds }),
  })
  if (!response.ok) throw new Error(await errorCode(response, 'integrator.importError'))
  return response.json() as Promise<{code:string;importedCount:number;updatedCount:number;skippedCount:number}>
}

export async function getIntegrationExecution(context: IntegratorContext,
  id: string): Promise<IntegrationExecutionDetail> {
  const response = await apiFetch(`${baseUrl(context)}/executions/${id}`, { credentials: 'include' })
  if (!response.ok) throw new Error(await errorCode(response, 'integrator.executionDetailError'))
  return response.json() as Promise<IntegrationExecutionDetail>
}

export async function getIntegrationExecutions(context: IntegratorContext, id:string, page:number,
  pageSize:number):Promise<IntegrationExecutionPage> {
  const query = new URLSearchParams({ page:String(page), pageSize:String(pageSize) })
  const response = await apiFetch(`${baseUrl(context)}/${id}/executions?${query}`,
    { credentials:'include' })
  if (!response.ok) throw new Error(await errorCode(response,
    'integrator.executionDetailError'))
  return response.json() as Promise<IntegrationExecutionPage>
}

export async function getRunningIntegrationExecutions(context: IntegratorContext, page:number,
  pageSize:number):Promise<IntegrationExecutionPage> {
  const query = new URLSearchParams({ page:String(page), pageSize:String(pageSize) })
  const response = await apiFetch(`${baseUrl(context)}/executions?${query}`,
    { credentials:'include' })
  if (!response.ok) throw new Error(await errorCode(response,
    'integrator.executionDetailError'))
  return response.json() as Promise<IntegrationExecutionPage>
}

export async function cancelIntegrationExecution(context: IntegratorContext,
  executionId:string):Promise<string> {
  const response = await apiFetch(`${baseUrl(context)}/executions/${executionId}/cancel`, {
    method:'POST', credentials:'include',
    headers:{ 'X-CSRF-TOKEN':await getCsrfToken() },
  })
  if (!response.ok) throw new Error(await errorCode(response,
    'integrator.executionCannotCancel'))
  const result = await response.json() as { code?:string }
  return result.code ?? 'integrator.executionCancellationRequested'
}

export async function getIntegrationStudio(context: IntegratorContext,
  options?:Record<string,string|number|undefined>): Promise<IntegrationStudioSnapshot> {
  const parameters = new URLSearchParams()
  if (options) Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== '') parameters.set(key, String(value))
  })
  const response = await apiFetch(`${baseUrl(context)}${parameters.size ? `?${parameters}` : ''}`, { credentials: 'include' })
  if (!response.ok) throw new Error(await errorCode(response, 'integrator.loadError'))
  return response.json() as Promise<IntegrationStudioSnapshot>
}

export async function inspectContract(context: IntegratorContext,
  form: IntegrationForm): Promise<ContractInspection> {
  const response = await apiFetch(`${baseUrl(context)}/inspect`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': await getCsrfToken(),
    },
    body: JSON.stringify({
      integrationId: form.id || null,
      apiDocumentationUrl: form.apiDocumentationUrl,
      authenticationType: form.authenticationType,
      authenticationUserName: form.authenticationUserName,
      authenticationSecret: form.authenticationSecret || null,
      apiKeyName: form.apiKeyName,
      apiKeyInQuery: form.apiKeyInQuery,
    }),
  })
  if (!response.ok) throw new Error(await errorCode(response, 'integrator.inspectError'))
  return response.json() as Promise<ContractInspection>
}

export async function saveIntegration(context: IntegratorContext,
  form: IntegrationForm): Promise<string> {
  const root = baseUrl(context)
  const response = await apiFetch(form.id ? `${root}/${form.id}` : root, {
    method: form.id ? 'PUT' : 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': await getCsrfToken(),
    },
    body: JSON.stringify({
      name: form.name,
      description: form.description,
      targetEntity: form.targetEntity,
      serviceBaseUrl: form.serviceBaseUrl,
      apiDocumentationUrl: form.apiDocumentationUrl,
      authenticationType: form.authenticationType,
      authenticationUserName: form.authenticationUserName,
      authenticationSecret: form.authenticationSecret || null,
      apiKeyName: form.apiKeyName,
      apiKeyInQuery: form.apiKeyInQuery,
      operationPath: form.operationPath,
      operationSummary: form.operationSummary,
      responseRootPath: form.responseRootPath,
      contractSnapshotJson: form.contractSnapshotJson,
      scheduleInterval: form.scheduleInterval,
      scheduleUnit: form.scheduleUnit,
      timeZoneId: form.timeZoneId,
      isScheduleEnabled: form.isScheduleEnabled,
      direction: form.direction,
      httpMethod: form.httpMethod,
      externalCompanyField: form.externalCompanyField,
      externalCompanyValue: form.externalCompanyValue,
      requestValues: form.requestValues,
      paginationStrategy: form.paginationStrategy,
      pageParameter: form.pageParameter,
      pageSizeParameter: form.pageSizeParameter,
      pageSize: form.pageSize,
      firstPage: form.firstPage,
      offsetParameter: form.offsetParameter,
      keysetFieldPath: form.keysetFieldPath,
      cursorParameter: form.cursorParameter,
      nextCursorPath: form.nextCursorPath,
      nextLinkPath: form.nextLinkPath,
      totalCountPath: form.totalCountPath,
      maximumPages: form.maximumPages,
      pageTimeoutSeconds: form.pageTimeoutSeconds,
      trigger: form.trigger,
      triggerStatus: form.triggerStatus,
      protectsMappedFields: form.protectsMappedFields,
      keyFields: form.keyFields,
      panelConfigurations: form.panelConfigurations,
      mappings: form.mappings,
    }),
  })
  if (!response.ok) throw new Error(await errorCode(response, 'integrator.saveError'))
  const payload = await response.json() as { code?: string }
  return payload.code ?? 'integrator.saved'
}

export async function testGet(context: IntegratorContext, form: IntegrationForm,
  readAllPages: boolean) {
  const response = await apiFetch(`${baseUrl(context)}/test-get`, { method: 'POST',
    credentials: 'include', headers: { 'Content-Type': 'application/json',
      'X-CSRF-TOKEN': await getCsrfToken() },
    body: JSON.stringify({ integrationId: form.id || null,
      definition: payload(form), readAllPages }) })
  if (!response.ok) throw new Error(await errorCode(response, 'integrator.testFailed'))
  return response.json() as Promise<{ succeeded:boolean; code:string; httpStatus:number;
    elapsedMilliseconds:number; pagesRead:number; recordsRead:number;
    finalRequestUrl:string; sampleJson:string }>
}

export async function integrationCommand(context: IntegratorContext, id: string,
  action: 'activate'|'pause'|'archive'|'restore'|'execute'): Promise<string> {
  const response = await apiFetch(`${baseUrl(context)}/${id}/${action}`, { method: 'POST',
    credentials: 'include', headers: { 'X-CSRF-TOKEN': await getCsrfToken() } })
  if (!response.ok) throw new Error(await errorCode(response, 'integrator.commandFailed'))
  const result = await response.json() as { code?: string }
  return result.code ?? 'integrator.commandSucceeded'
}
